import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useSubscription } from '@features/subscriptions';
import { useUserRoles } from '@features/user';
import type { SxProps, Theme } from '@mui/material';
import { useModal } from '@store/hooks';
import { ROLES } from '@utils/roleUtils';

import { EntitlementUsageCard } from '../components/EntitlementUsageCard';
import { useEntitlement } from '../store/api';

/**
 * Capabilities that should show EntitlementUsageCard even with single-tier catalog.
 * These are features where showing usage/limits is valuable independent of upgrades.
 */
const ALWAYS_SHOW_CAPABILITIES = new Set(['audit_suggestions']);

export interface EntitlementUsageProps {
  /** The capability name to display (e.g., 'notes', 'labs') */
  capabilityName: string;

  /** Source for analytics tracking (e.g., 'notes_list', 'encounter_create') */
  source?: string;

  /** Pass-through props for EntitlementUsageCard */
  showUpgradeLink?: boolean;
  variant?: 'compact' | 'standard';
  noBorderRadius?: boolean;

  /** Optional sx props for wrapper styling */
  sx?: SxProps<Theme>;
}

/**
 * EntitlementUsage Component
 *
 * Container/orchestrator component that:
 * - Checks the 'feature_bundling' Statsig feature gate
 * - Fetches user entitlements from dedicated entitlements endpoint
 * - Finds the entitlement matching the provided capability name
 * - Renders EntitlementUsageCard with the found entitlement
 * - Shows upgrade link for all users (admins navigate, non-admins see contact admin modal)
 *
 * Returns null if:
 * - Feature gate is not passing
 * - Entitlement is not found
 * - Entitlements are still loading (shows loading state via EntitlementUsageCard)
 * - Single-tier catalog AND capability is not in allowlist (e.g., audit_suggestions)
 *
 * @example
 * ```tsx
 * <EntitlementUsage capabilityName="note_creation" />
 * ```
 */
export const EntitlementUsage = memo<EntitlementUsageProps>(
  ({
    capabilityName,
    source,
    showUpgradeLink = true,
    variant = 'standard',
    noBorderRadius = false,
    sx,
  }) => {
    const navigate = useNavigate();
    const roles = useUserRoles();
    const { open } = useModal();

    // Check if user has practice_administrator role
    const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
    // Check if user has clinician role
    // TODO: Re-enable after backend entitlement change
    // const isClinician = roles.includes(ROLES.CLINICIAN);
    const isClinician = true; // Temporarily default to true

    // Create upgrade handler: navigate for admins, show modal for non-admins
    const handleUpgradeClick = isPracticeAdmin
      ? () => navigate('/practice/clinicians')
      : () => open(MODAL_TYPES.ENTITLEMENT_CONTACT_ADMIN);

    // Get entitlement using dedicated entitlements endpoint
    // refetchOnMountOrArgChange ensures fresh data when component mounts (e.g., after note generation)
    // refetchOnFocus ensures fresh data when user returns to the browser tab
    // Note: We intentionally only use isLoading (not isFetching) to implement stale-while-revalidate
    // pattern - showing cached data during background refetches instead of loading skeletons
    const { entitlement, isLoading, isFeatureEnabled, isSingleTier } = useEntitlement(
      capabilityName,
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
      },
    );

    // Get subscription details to check if user is trialing
    const { subscription } = useSubscription();
    const isTrialing = subscription?.subscription_status === 'trialing';

    if (!isFeatureEnabled) {
      return null;
    }

    // Hide card for single-tier catalogs unless capability is in allowlist
    if (isSingleTier && !ALWAYS_SHOW_CAPABILITIES.has(capabilityName)) {
      return null;
    }

    // Return null if entitlement not found (but not while loading)
    if (!isLoading && !entitlement) {
      return null;
    }

    // Render EntitlementUsageCard with found entitlement
    return (
      <EntitlementUsageCard
        entitlement={entitlement}
        onUpgradeClick={handleUpgradeClick}
        showUpgradeLink={showUpgradeLink}
        variant={variant}
        loading={isLoading}
        noBorderRadius={noBorderRadius}
        source={source}
        isTrialing={isTrialing}
        isNonClinician={!isClinician}
        sx={sx}
      />
    );
  },
);

EntitlementUsage.displayName = 'EntitlementUsage';
